import { createContext, PropsWithChildren, useCallback, useContext, useMemo } from "react";
import { UseMutateFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { HydraErrorResponse } from "@/types";
import { OnboardingStepsStatus } from "../../onboarding/types";
import { EditSettingsRequestPayload, SettingsSuccessResponse } from "../types";
import useEditSettings from "../hooks/use-edit-settings";
import { useSettingsList } from "../hooks/use-settings-list";

const SettingsContext = createContext<{
  edit: UseMutateFunction<SettingsSuccessResponse, AxiosError<HydraErrorResponse>, EditSettingsRequestPayload>;
  editWithoutNotification: UseMutateFunction<
    SettingsSuccessResponse,
    AxiosError<HydraErrorResponse>,
    EditSettingsRequestPayload
  >;
  settings: SettingsSuccessResponse | undefined;
  isFetching: boolean;
  isSubmitting: boolean;
  isSubmittingSilently: boolean;
  changeStep: (newStatus: OnboardingStepsStatus) => void;
} | null>(null);

const SettingsProvider = ({ children }: PropsWithChildren) => {
  const { data: settings, isFetching } = useSettingsList();

  const id = useMemo(() => {
    if (settings) return settings.id || null;

    return null;
  }, [settings]);

  const { mutate, isPending: isSubmitting } = useEditSettings(id);

  const { mutate: editWithoutNotification, isPending: isSubmittingSilently } = useEditSettings(id, false);

  const changeStep = useCallback(
    (newStatus: OnboardingStepsStatus) => {
      if (!settings?.settingsData) return;

      const payload = {
        settingsData: {
          ...settings.settingsData,
          onboardingStatus: newStatus,
        },
      };
      editWithoutNotification(payload);
    },
    [editWithoutNotification, settings?.settingsData],
  );

  const contextValue = useMemo(
    () => ({
      edit: mutate,
      editWithoutNotification,
      settings,
      isFetching,
      isSubmitting,
      isSubmittingSilently,
      changeStep,
    }),
    [mutate, editWithoutNotification, settings, isFetching, isSubmitting, isSubmittingSilently, changeStep],
  );

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(`useSettings must be used within SettingsProvider`);
  }
  return context;
};

export { SettingsProvider, useSettings };
